import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
 
import {
  Plus,
  Search,
  Filter,
  Download,
  DollarSign,
  Calendar,
  AlertCircle,
  CheckCircle,
  Clock,
  Users,
  Bell,
  Link as LinkIcon,
  FileText,
  PieChart,
} from "lucide-react";
import { BarChart, Bar, PieChart as RechartsPie, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { db, auth } from "@/firebase/config";
import { collection, getDocs, query, where, addDoc, serverTimestamp } from "firebase/firestore";
import { onAuthStateChanged, getIdTokenResult } from "firebase/auth";
import { Loader2 } from "lucide-react";

const PledgeTracking = () => {
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [purposeFilter, setPurposeFilter] = useState("all");
  const [schoolID, setSchoolID] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [pledges, setPledges] = useState<any[]>([]);
  const [reloadVersion, setReloadVersion] = useState(0);

  // New pledge dialog state
  const [newOpen, setNewOpen] = useState(false);
  const [npName, setNpName] = useState("");
  const [npContact, setNpContact] = useState("");
  const [npEmail, setNpEmail] = useState("");
  const [npAmount, setNpAmount] = useState<string>("");
  const [npPurpose, setNpPurpose] = useState<string>("");
  const [npDueDate, setNpDueDate] = useState<string>("");
  const [npStudent, setNpStudent] = useState<string>("");
  const [npNotes, setNpNotes] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [npErrors, setNpErrors] = useState<Record<string,string>>({});

  // Derive schoolID from auth claims
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) { setSchoolID(null); return; }
      try {
        const token = await getIdTokenResult(u, true);
        const affiliations = (token.claims.affiliations as Array<{ role: string; schoolID?: string }> | undefined) || [];
        const defaultAff = token.claims.defaultAffiliation as { role?: string; schoolID?: string } | undefined;
        const pick =
          (affiliations.find(a => a.role === 'admin' && a.schoolID?.trim()) ||
           affiliations.find(a => a.role === 'school_staff' && a.schoolID?.trim()) ||
           defaultAff);
        const sid = pick?.schoolID?.trim() || null;
        setSchoolID(sid);
      } catch {
        setSchoolID(null);
      }
    });
    return () => unsub();
  }, []);

  // Fetch pledges and aggregate collected amounts from transactions by matching student registration number
  // Payments with accountReference = "Fee-{studentRegNumber}" automatically count toward the pledge
  useEffect(() => {
    const load = async () => {
      if (!schoolID) { setPledges([]); return; }
      setLoading(true);
      try {
        const snap = await getDocs(collection(db, `schools/${schoolID}/pledges`));
        const arr: any[] = [];
        for (const d of snap.docs) {
          const data = d.data();
          // Normalize core fields
          const amountPledged = Number((data as any)?.amountPledged ?? 0) || 0;
          const studentLinked = String((data as any)?.studentLinked || '').trim();
          let dueDateStr: string | null = null;
          const rawDue = (data as any)?.dueDate;
          if (rawDue && typeof rawDue?.toDate === 'function') {
            const dt = rawDue.toDate();
            dueDateStr = `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`;
          } else if (typeof rawDue === 'string') {
            const dt = new Date(rawDue);
            if (!isNaN(dt.getTime())) {
              dueDateStr = `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`;
            }
          }
          const base: any = { id: d.id, ...data, amountPledged, dueDate: dueDateStr, studentLinked };
          
          // Aggregate payments automatically: any payment with accountReference "Fee-{studentLinked}" counts toward this pledge
          let collected = 0;
          if (studentLinked) {
            const providers = ['mpesa','equity','kcb','cash','card'];
            for (const p of providers) {
              try {
                const qRef = query(
                  collection(db, `schools/${schoolID}/transactions/${p}/records`),
                  where('status','==','completed'),
                  where('accountReference','==', `Fee-${studentLinked}`)
                );
                const tx = await getDocs(qRef);
                tx.forEach(txd => {
                  const t = txd.data() as any;
                  const amt = Number(t?.amount || 0) || 0;
                  const type = String(t?.type || 'credit').toLowerCase();
                  collected += type === 'debit' ? -amt : amt;
                });
              } catch {}
            }
          }
          base.amountCollected = collected;
          
          // Derive status dynamically: completed | partial | pending | overdue
          const today = new Date();
          const dueDate = base.dueDate ? new Date(base.dueDate) : null;
          let derivedStatus = base.status as string | undefined;
          if (!derivedStatus) {
            if (collected >= amountPledged && amountPledged > 0) {
              derivedStatus = 'completed';
            } else if (dueDate && !isNaN(dueDate.getTime()) && dueDate < new Date(today.getFullYear(), today.getMonth(), today.getDate()) && collected < amountPledged) {
              derivedStatus = 'overdue';
            } else if (collected > 0 && collected < amountPledged) {
              derivedStatus = 'partial';
            } else {
              derivedStatus = 'pending';
            }
          }
          base.status = derivedStatus;
          arr.push(base);
        }
        setPledges(arr);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [schoolID, reloadVersion]);

  // Dashboard statistics
  const totalPledged = pledges.reduce((sum, p) => sum + p.amountPledged, 0);
  const totalCollected = pledges.reduce((sum, p) => sum + p.amountCollected, 0);
  const pendingAmount = totalPledged - totalCollected;
  const fulfillmentRate = ((totalCollected / totalPledged) * 100).toFixed(1);
  const overduePledges = pledges.filter(p => p.status === "overdue").length;
  const upcomingDue = pledges.filter(p => {
    const dueDate = new Date(p.dueDate);
    const today = new Date();
    const diffDays = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return diffDays > 0 && diffDays <= 7 && p.status !== "completed";
  }).length;

  // Chart data
  const statusChartData = [
    { name: "Completed", value: pledges.filter(p => p.status === "completed").length, color: "#10b981" },
    { name: "Partial", value: pledges.filter(p => p.status === "partial").length, color: "#f59e0b" },
    { name: "Pending", value: pledges.filter(p => p.status === "pending").length, color: "#6b7280" },
    { name: "Overdue", value: pledges.filter(p => p.status === "overdue").length, color: "#ef4444" },
  ];

  const purposeData = [
    { purpose: "Tuition", amount: pledges.filter(p => p.purpose === "Tuition").reduce((sum, p) => sum + p.amountPledged, 0) },
    { purpose: "Library", amount: pledges.filter(p => p.purpose === "Library Donation").reduce((sum, p) => sum + p.amountPledged, 0) },
    { purpose: "Sports", amount: pledges.filter(p => p.purpose === "Sports Event").reduce((sum, p) => sum + p.amountPledged, 0) },
    { purpose: "Building", amount: pledges.filter(p => p.purpose === "Building Fund").reduce((sum, p) => sum + p.amountPledged, 0) },
  ];

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400"><CheckCircle className="w-3 h-3 mr-1" /> Completed</Badge>;
      case 'partial':
        return <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400"><Clock className="w-3 h-3 mr-1" /> Partially Fulfilled</Badge>;
      case 'pending':
        return <Badge className="bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400"><Calendar className="w-3 h-3 mr-1" /> Pending</Badge>;
      case 'overdue':
        return <Badge className="bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400"><AlertCircle className="w-3 h-3 mr-1" /> Overdue</Badge>;
      default:
        return <Badge variant="secondary">Unknown</Badge>;
    }
  };

  const handleSaveNewPledge = async () => {
    if (!schoolID) return;
    // Basic validation with inline errors
    const errors: Record<string,string> = {};
    const amountNum = Number(npAmount || 0);
    if (!npName.trim()) errors.pledgerName = 'Pledger name is required';
    if (!npContact.trim()) errors.contact = 'Contact number is required';
    if (!npPurpose) errors.purpose = 'Purpose is required';
    if (!npDueDate) errors.dueDate = 'Due date is required';
    if (!npStudent.trim()) errors.studentLinked = 'Student registration number is required';
    if (!amountNum || amountNum <= 0) errors.amountPledged = 'Amount must be greater than zero';

    if (Object.keys(errors).length > 0) {
      setNpErrors(errors);
      return;
    }

    setNpErrors({});
    setSaving(true);
    try {
      const payload: any = {
        pledgerName: npName.trim(),
        contact: npContact.trim(),
        email: npEmail.trim() || null,
        amountPledged: amountNum,
        purpose: npPurpose,
        // store dueDate as JS Date so Firestore saves as timestamp
        dueDate: new Date(npDueDate),
        studentLinked: npStudent.trim(),
        notes: npNotes || null,
        dateCreated: serverTimestamp(),
      };
      const ref = await addDoc(collection(db, `schools/${schoolID}/pledges`), payload);
      console.log('Pledge saved with ID:', ref.id);
      // trigger reload and close, show success
      setReloadVersion(v => v + 1);
      // clear form
      setNpName(""); setNpContact(""); setNpEmail(""); setNpAmount(""); setNpPurpose(""); setNpDueDate(""); setNpStudent(""); setNpNotes("");
      setNewOpen(false);
      toast({
        title: "Success",
        description: "Pledge saved successfully",
      });
    } catch (err) {
      console.error('Failed to save pledge', err);
      toast({
        title: "Error",
        description: "Failed to save pledge. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const filteredPledges = pledges.filter(pledge => {
    const name = String(pledge.pledgerName || '').toLowerCase();
    const contact = String(pledge.contact || '');
    const student = String(pledge.studentLinked || '').toLowerCase();
    const pStatus = String(pledge.status || '').toLowerCase();
    const pPurpose = String(pledge.purpose || '');
    const matchesSearch = name.includes(searchTerm.toLowerCase()) ||
                         contact.includes(searchTerm) ||
                         student.includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || pStatus === statusFilter;
    const matchesPurpose = purposeFilter === "all" || pPurpose === purposeFilter;
    return matchesSearch && matchesStatus && matchesPurpose;
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      <style>{`
        @media (max-width: 768px) {
          body { overflow-x: hidden; }
          .mobile-table-container {
            width: 100vw;
            margin-left: calc(-50vw + 50%);
            padding-left: 16px;
            padding-right: 16px;
            box-sizing: border-box;
          }
          .mobile-table-container table thead th:first-child {
            padding-left: 16px;
          }
        }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
      <Tabs defaultValue="dashboard" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="pledges">All Pledges</TabsTrigger>
          <TabsTrigger value="reports">Reports</TabsTrigger>
          <TabsTrigger value="reminders">Reminders</TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* Summary Cards */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Pledged</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatCurrency(totalPledged)}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  From {pledges.length} pledges
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Amount Collected</CardTitle>
                <CheckCircle className="h-4 w-4 text-green-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">{formatCurrency(totalCollected)}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  {fulfillmentRate}% fulfillment rate
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Pending Amount</CardTitle>
                <Clock className="h-4 w-4 text-yellow-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-yellow-600">{formatCurrency(pendingAmount)}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  Still to be collected
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Alerts</CardTitle>
                <AlertCircle className="h-4 w-4 text-red-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  <span className="text-red-600">{overduePledges}</span>
                  <span className="text-muted-foreground text-lg mx-2">/</span>
                  <span className="text-yellow-600">{upcomingDue}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Overdue / Due in 7 days
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Charts */}
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Pledge Status Distribution</CardTitle>
                <CardDescription>Breakdown by fulfillment status</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <RechartsPie>
                    <Pie
                      data={statusChartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${((percent as number) * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {statusChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </RechartsPie>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Pledges by Purpose</CardTitle>
                <CardDescription>Total amount pledged per category</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={purposeData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="purpose" />
                    <YAxis />
                    <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                    <Legend />
                    <Bar dataKey="amount" fill="hsl(var(--primary))" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Recent Activity & Alerts */}
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-red-500" />
                  Overdue Pledges
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {pledges.filter(p => p.status === "overdue").map(pledge => (
                    <div key={pledge.id} className="flex items-start justify-between border-l-2 border-red-500 pl-3 py-2">
                      <div>
                        <p className="font-medium">{pledge.pledgerName}</p>
                        <p className="text-sm text-muted-foreground">{pledge.purpose}</p>
                        <p className="text-xs text-muted-foreground">Due: {pledge.dueDate}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-red-600">{formatCurrency(pledge.amountPledged)}</p>
                      </div>
                    </div>
                  ))}
                  {pledges.filter(p => p.status === "overdue").length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">No overdue pledges</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="h-5 w-5 text-yellow-500" />
                  Upcoming Due (7 days)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {pledges.filter(p => {
                    const dueDate = new Date(p.dueDate);
                    const today = new Date();
                    const diffDays = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                    return diffDays > 0 && diffDays <= 7 && p.status !== "completed";
                  }).map(pledge => (
                    <div key={pledge.id} className="flex items-start justify-between border-l-2 border-yellow-500 pl-3 py-2">
                      <div>
                        <p className="font-medium">{pledge.pledgerName}</p>
                        <p className="text-sm text-muted-foreground">{pledge.purpose}</p>
                        <p className="text-xs text-muted-foreground">Due: {pledge.dueDate}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-yellow-600">{formatCurrency(pledge.amountPledged - pledge.amountCollected)}</p>
                      </div>
                    </div>
                  ))}
                  {pledges.filter(p => {
                    const dueDate = new Date(p.dueDate);
                    const today = new Date();
                    const diffDays = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                    return diffDays > 0 && diffDays <= 7 && p.status !== "completed";
                  }).length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">No upcoming due pledges</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* All Pledges Tab */}
        <TabsContent value="pledges" className="space-y-4">
          {/* Header with Actions */}
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <div>
              <h2 className="text-2xl font-bold">Pledge Management</h2>
              <p className="text-muted-foreground">Track and manage all pledges</p>
            </div>
            <div className="flex gap-2">
              <Dialog open={newOpen} onOpenChange={setNewOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-primary text-primary-foreground shadow-md hover:shadow-lg hover:bg-primary/90">
                    <Plus className="mr-2 h-4 w-4" />
                    New Pledge
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto border border-border rounded-lg shadow-xl">
                  <DialogHeader>
                    <DialogTitle>Record New Pledge</DialogTitle>
                    <DialogDescription>
                      Add details of a new pledge from a parent, guardian, or donor
                    </DialogDescription>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="pledger-name">Pledger Name *</Label>
                        <Input id="pledger-name" placeholder="Enter full name" value={npName} onChange={e=>setNpName(e.target.value)} />
                        {npErrors.pledgerName && <p className="text-xs text-destructive mt-1">{npErrors.pledgerName}</p>}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="contact">Contact Number *</Label>
                        <Input id="contact" placeholder="+254-700-000000" value={npContact} onChange={e=>setNpContact(e.target.value)} />
                        {npErrors.contact && <p className="text-xs text-destructive mt-1">{npErrors.contact}</p>}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address</Label>
                      <Input id="email" type="email" placeholder="email@example.com" value={npEmail} onChange={e=>setNpEmail(e.target.value)} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="amount">Amount Pledged *</Label>
                        <Input id="amount" type="number" placeholder="0" value={npAmount} onChange={e=>setNpAmount(e.target.value)} />
                        {npErrors.amountPledged && <p className="text-xs text-destructive mt-1">{npErrors.amountPledged}</p>}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="purpose">Purpose *</Label>
                        <Select value={npPurpose} onValueChange={setNpPurpose}>
                          <SelectTrigger id="purpose">
                            <SelectValue placeholder="Select purpose" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Tuition">Tuition</SelectItem>
                            <SelectItem value="Library Donation">Library Donation</SelectItem>
                            <SelectItem value="Sports Event">Sports Event</SelectItem>
                            <SelectItem value="Building Fund">Building Fund</SelectItem>
                            <SelectItem value="School Events">School Events</SelectItem>
                            <SelectItem value="Other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                        {npErrors.purpose && <p className="text-xs text-destructive mt-1">{npErrors.purpose}</p>}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="due-date">Due Date *</Label>
                        <Input id="due-date" type="date" value={npDueDate} onChange={e=>setNpDueDate(e.target.value)} />
                        {npErrors.dueDate && <p className="text-xs text-destructive mt-1">{npErrors.dueDate}</p>}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="student-link">Link to Student *</Label>
                      <Input id="student-link" placeholder="Enter student registration number" value={npStudent} onChange={e=>setNpStudent(e.target.value)} />
                      {npErrors.studentLinked && <p className="text-xs text-destructive mt-1">{npErrors.studentLinked}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="notes">Notes / Special Instructions</Label>
                      <Textarea id="notes" placeholder="Add any additional notes..." rows={3} value={npNotes} onChange={e=>setNpNotes(e.target.value)} />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setNewOpen(false)}>Cancel</Button>
                    <Button onClick={handleSaveNewPledge} disabled={saving}>
                      {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Save Pledge
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              <Button variant="outline">
                <Download className="mr-2 h-4 w-4" />
                Export
              </Button>
            </div>
          </div>

          {/* Filters */}
          <Card>
            <CardContent className="pt-6">
              <div className="grid gap-4 md:grid-cols-4">
                <div className="space-y-2">
                  <Label htmlFor="search">Search</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="search"
                      placeholder="Search pledger or student..."
                      className="pl-10"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="status-filter">Status</Label>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger id="status-filter">
                      <SelectValue placeholder="All statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="partial">Partially Fulfilled</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="overdue">Overdue</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="purpose-filter">Purpose</Label>
                  <Select value={purposeFilter} onValueChange={setPurposeFilter}>
                    <SelectTrigger id="purpose-filter">
                      <SelectValue placeholder="All purposes" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Purposes</SelectItem>
                      <SelectItem value="Tuition">Tuition</SelectItem>
                      <SelectItem value="Library Donation">Library</SelectItem>
                      <SelectItem value="Sports Event">Sports</SelectItem>
                      <SelectItem value="Building Fund">Building</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>&nbsp;</Label>
                  <Button variant="outline" className="w-full">
                    <Filter className="mr-2 h-4 w-4" />
                    Advanced Filters
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pledges Table */}
          <Card>
            <CardContent className="pt-6">
              <div className="mobile-table-container overflow-x-auto no-scrollbar">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-[150px]">Pledger</TableHead>
                      <TableHead className="min-w-[120px]">Contact</TableHead>
                      <TableHead className="min-w-[100px]">Purpose</TableHead>
                      <TableHead className="min-w-[120px]">Amount Pledged</TableHead>
                      <TableHead className="min-w-[100px]">Collected</TableHead>
                      <TableHead className="min-w-[100px]">Balance</TableHead>
                      <TableHead className="min-w-[140px]">Status</TableHead>
                      <TableHead className="min-w-[100px]">Due Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-6">
                          <Loader2 className="h-5 w-5 animate-spin inline-block mr-2" />
                          Loading pledges...
                        </TableCell>
                      </TableRow>
                    ) : filteredPledges.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-6 text-muted-foreground">
                          No pledges found
                        </TableCell>
                      </TableRow>
                    ) : filteredPledges.map((pledge) => (
                      <TableRow key={pledge.id}>
                        <TableCell>
                          <div className="max-w-[150px]">
                            <p className="font-medium truncate">{pledge.pledgerName}</p>
                            {pledge.studentLinked && (
                              <p className="text-xs text-muted-foreground flex items-center gap-1 truncate">
                                <LinkIcon className="h-3 w-3 flex-shrink-0" />
                                <span className="truncate">{pledge.studentLinked}</span>
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm max-w-[120px]">
                            <p className="truncate">{pledge.contact}</p>
                            {pledge.email && <p className="text-muted-foreground truncate">{pledge.email}</p>}
                          </div>
                        </TableCell>
                        <TableCell className="max-w-[100px] truncate">{pledge.purpose}</TableCell>
                        <TableCell className="font-medium whitespace-nowrap">{formatCurrency(pledge.amountPledged)}</TableCell>
                        <TableCell className="text-green-600 whitespace-nowrap">{formatCurrency(pledge.amountCollected)}</TableCell>
                        <TableCell className="font-medium whitespace-nowrap">{formatCurrency(pledge.amountPledged - pledge.amountCollected)}</TableCell>
                        <TableCell>{getStatusBadge(pledge.status)}</TableCell>
                        <TableCell className="whitespace-nowrap">{pledge.dueDate}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="mt-4 p-3 bg-muted/50 rounded-md text-sm text-muted-foreground">
                <p className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span>Pledge payments are tracked automatically. When a student pays their fees (using Fee-[StudentRegNumber] format), it counts toward their pledge.</span>
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Generate Pledge Reports</CardTitle>
              <CardDescription>Create detailed reports for analysis and audits</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Fulfillment Report</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label>Date Range</Label>
                      <div className="grid grid-cols-2 gap-2">
                        <Input type="date" />
                        <Input type="date" />
                      </div>
                    </div>
                    <Button className="w-full">
                      <FileText className="mr-2 h-4 w-4" />
                      Generate Report
                    </Button>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Top Contributors Report</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label>Minimum Amount</Label>
                      <Input type="number" placeholder="e.g., 100000" />
                    </div>
                    <Button className="w-full">
                      <Users className="mr-2 h-4 w-4" />
                      Generate Report
                    </Button>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Purpose-wise Summary</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label>Select Purpose</Label>
                      <Select>
                        <SelectTrigger>
                          <SelectValue placeholder="All purposes" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Purposes</SelectItem>
                          <SelectItem value="tuition">Tuition</SelectItem>
                          <SelectItem value="library">Library</SelectItem>
                          <SelectItem value="sports">Sports</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button className="w-full">
                      <PieChart className="mr-2 h-4 w-4" />
                      Generate Report
                    </Button>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Overdue Pledges Report</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="p-3 bg-red-50 dark:bg-red-900/10 rounded-md">
                      <p className="text-sm text-red-600 dark:text-red-400">
                        Currently {overduePledges} overdue pledge(s)
                      </p>
                    </div>
                    <Button className="w-full" variant="destructive">
                      <AlertCircle className="mr-2 h-4 w-4" />
                      Generate Report
                    </Button>
                  </CardContent>
                </Card>
              </div>

              <div className="pt-4 border-t">
                <h3 className="font-semibold mb-4">Export Options</h3>
                <div className="flex gap-2">
                  <Button variant="outline">
                    <Download className="mr-2 h-4 w-4" />
                    Export to PDF
                  </Button>
                  <Button variant="outline">
                    <Download className="mr-2 h-4 w-4" />
                    Export to Excel
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reminders Tab */}
        <TabsContent value="reminders" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Automated Reminders & Notifications</CardTitle>
              <CardDescription>Manage SMS and email reminders for pledgers</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                 
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Bell className="h-5 w-5" />
                      Send Reminder
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label>Select Recipients</Label>
                      <Select>
                        <SelectTrigger>
                          <SelectValue placeholder="Choose pledgers..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="overdue">All Overdue Pledges</SelectItem>
                          <SelectItem value="upcoming">Upcoming Due (7 days)</SelectItem>
                          <SelectItem value="partial">Partially Fulfilled</SelectItem>
                          <SelectItem value="all-pending">All Pending</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Notification Type</Label>
                      <div className="flex gap-2">
                        <Button variant="outline" className="flex-1">SMS</Button>
                        <Button variant="outline" className="flex-1">Email</Button>
                        <Button variant="outline" className="flex-1">Both</Button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Message Template</Label>
                      <Textarea 
                        placeholder="Dear [Name], this is a reminder about your pledge of [Amount]..."
                        rows={4}
                      />
                    </div>
                    <Button className="w-full">
                      <Bell className="mr-2 h-4 w-4" />
                      Send Reminders
                    </Button>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Automated Reminders Settings</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 border rounded-md">
                        <div>
                          <p className="font-medium text-sm">7-day advance reminder</p>
                          <p className="text-xs text-muted-foreground">Send reminder 7 days before due date</p>
                        </div>
                        <Button variant="outline" size="sm">Enable</Button>
                      </div>
                      <div className="flex items-center justify-between p-3 border rounded-md">
                        <div>
                          <p className="font-medium text-sm">Due date reminder</p>
                          <p className="text-xs text-muted-foreground">Send reminder on due date</p>
                        </div>
                        <Button variant="outline" size="sm">Enable</Button>
                      </div>
                      <div className="flex items-center justify-between p-3 border rounded-md">
                        <div>
                          <p className="font-medium text-sm">Overdue follow-up</p>
                          <p className="text-xs text-muted-foreground">Send reminder 3 days after due date</p>
                        </div>
                        <Button variant="outline" size="sm">Enable</Button>
                      </div>
                      <div className="flex items-center justify-between p-3 border rounded-md">
                        <div>
                          <p className="font-medium text-sm">Thank you message</p>
                          <p className="text-xs text-muted-foreground">Auto-send when pledge is fulfilled</p>
                        </div>
                        <Button variant="outline" size="sm">Enable</Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Recent Notifications</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date Sent</TableHead>
                        <TableHead>Recipient</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Message</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell>2024-03-01 10:30</TableCell>
                        <TableCell>Emily Brown</TableCell>
                        <TableCell><Badge variant="outline">SMS</Badge></TableCell>
                        <TableCell className="max-w-xs truncate">Reminder: Your pledge of UGX 200,000 is overdue...</TableCell>
                        <TableCell><Badge className="bg-green-100 text-green-800">Sent</Badge></TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>2024-02-28 09:15</TableCell>
                        <TableCell>Michael Johnson</TableCell>
                        <TableCell><Badge variant="outline">Email</Badge></TableCell>
                        <TableCell className="max-w-xs truncate">Your pledge payment is due in 7 days...</TableCell>
                        <TableCell><Badge className="bg-green-100 text-green-800">Sent</Badge></TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>2024-02-25 14:00</TableCell>
                        <TableCell>John Doe</TableCell>
                        <TableCell><Badge variant="outline">SMS</Badge></TableCell>
                        <TableCell className="max-w-xs truncate">Thank you for fulfilling your pledge!</TableCell>
                        <TableCell><Badge className="bg-green-100 text-green-800">Sent</Badge></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PledgeTracking;